/**
 * InfinitSales - Follow-Up Service
 * API service for follow-up challenge sequences
 */
import api from './api';
import requestCache from './requestCache';

const BASE = '/api/followups';

// Cache in-memory para getLimits — TTL gerenciado dentro do metodo. Mantido
// fora da classe para que todas as instancias compartilhem o mesmo cache.
let _limitsCache = null;

class FollowUpService {
  /** List all follow-ups (lightweight, no full days data) */
  async list(status = null, type = null) {
    const params = {};
    if (status) params.status = status;
    if (type) params.type = type;
    const response = await api.get(BASE, { params });
    return response.data;
  }

  /** Get follow-up by ID (full data with days/messages) */
  async getById(id) {
    const response = await api.get(`${BASE}/${id}`);
    return response.data;
  }

  /** Create new follow-up */
  async create(data) {
    const response = await api.post(BASE, data);
    requestCache.invalidate('followupsByRecipient:');
    return response.data;
  }

  /** Update follow-up (partial) */
  async update(id, data) {
    const response = await api.put(`${BASE}/${id}`, data);
    requestCache.invalidate('followupsByRecipient:');
    return response.data;
  }

  /** Delete follow-up */
  async delete(id) {
    const response = await api.delete(`${BASE}/${id}`);
    requestCache.invalidate('followupsByRecipient:');
    return response.data;
  }

  /** Update status (activate or pause) */
  async updateStatus(id, status) {
    const response = await api.put(`${BASE}/${id}/status`, { status });
    requestCache.invalidate('followupsByRecipient:');
    return response.data;
  }

  /** Send messages for a specific day to specific recipients */
  async sendDay(id, day, recipients, isTest = false) {
    const response = await api.post(`${BASE}/${id}/send-day`, {
      day,
      recipients,
      is_test: isTest,
    });
    return response.data;
  }

  /** Get send history */
  async getHistory(id, day = null, recipient = null) {
    const params = {};
    if (day) params.day = day;
    if (recipient) params.recipient = recipient;
    const response = await api.get(`${BASE}/${id}/history`, { params });
    return response.data;
  }

  /** Reset history for a specific recipient */
  async resetHistoryByRecipient(id, phone) {
    const response = await api.delete(`${BASE}/${id}/history/recipient/${phone}`);
    return response.data;
  }

  /** Remove a recipient from the follow-up */
  async removeRecipient(id, phone) {
    const response = await api.delete(`${BASE}/${id}/recipients/${phone}`);
    requestCache.invalidate('followupsByRecipient:');
    return response.data;
  }

  /** Reset history for a specific day */
  async resetHistoryByDay(id, day) {
    const response = await api.delete(`${BASE}/${id}/history/day/${day}`);
    return response.data;
  }

  /** Schedule a follow-up message for a specific contact (from Inbox) */
  async scheduleForContact(followupId, { phone, send_time, send_date, recurrence }) {
    const response = await api.post(`${BASE}/${followupId}/schedule-for-contact`, {
      phone,
      send_time,
      send_date,
      recurrence: recurrence || 'once',
    });
    requestCache.invalidate('followupsByRecipient:');
    return response.data;
  }

  /** Send scheduled message immediately (manual trigger / test) */
  async sendNow(followupId) {
    const response = await api.post(`${BASE}/${followupId}/send-now`);
    return response.data;
  }

  /** Quick create and schedule a follow-up from Inbox (inline message) */
  async quickCreate(data) {
    const response = await api.post(`${BASE}/quick-create`, data);
    requestCache.invalidate('followupsByRecipient:');
    return response.data;
  }

  /**
   * Get followup limits for the current user's plan.
   *
   * Cache in-memory TTL 5min: o endpoint internamente chama a InfinitAgents
   * (1-3s de latencia) e o plano nao muda toda hora. Sem isso, todo abrir
   * do modal "Agendar mensagem" gerava delay perceptivel + flash de badge
   * PRO falso no botao "Mensagem com IA" para contas Business.
   */
  async getLimits({ forceRefresh = false } = {}) {
    const now = Date.now();
    if (!forceRefresh && _limitsCache && now < _limitsCache.expiresAt) {
      return _limitsCache.payload;
    }
    const response = await api.get(`${BASE}/limits`);
    _limitsCache = {
      payload: response.data,
      expiresAt: now + 5 * 60 * 1000,
    };
    return response.data;
  }

  /** Limpa o cache de limits — chamar quando o webhook de upgrade chegar. */
  invalidateLimitsCache() {
    _limitsCache = null;
  }

  /** Get all active follow-ups for a specific recipient phone.
   *  Cache curto por telefone + dedup em voo: o FollowupBadge do header e a
   *  seção de follow-ups do drawer pediam o mesmo dado 2-3x por abertura de
   *  conversa (e o backend varre os followups ativos a cada chamada). */
  async getByRecipient(phone) {
    return requestCache.cachedGet(
      `followupsByRecipient:${phone}`,
      async () => {
        const response = await api.get(`${BASE}/by-recipient/${encodeURIComponent(phone)}`);
        return response.data;
      },
      { ttl: 20_000 },
    );
  }

  /**
   * Check whether the WhatsApp Business 24h messaging window is open for a contact.
   * Returns { is_open: boolean, reason?: 'no_chat'|'no_inbound'|'expired'|'no_business_account' }
   */
  async checkRecipientWindow(phone) {
    const response = await api.post(`${BASE}/check-recipient-window`, { phone });
    return response.data;
  }
}

const followupService = new FollowUpService();
export default followupService;
